import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Fab,
  TextField,
  MenuItem,
  LinearProgress,
  Chip,
  Box,
  Skeleton,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  PlayCircle as ContinueIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import Stocktakes from "../../collections/stocktakes";
import { transitionStocktake } from "../../collections/stocktakes";
import CreateStocktakeDialog from './CreateStocktakeDialog';

const StocktakeList = (props) => {
  const navigate = useNavigate();
  const [stocktakes, setStocktakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  // Get user role from props or context
  const userRole = props.controlRole || 'STAFF'; // Default to STAFF

  useEffect(() => {
    props.setPageTitle("Kiểm kho hàng ngày");
    fetchStocktakes();

    // Set up auto-refresh every 30 seconds for real-time updates
    const interval = setInterval(() => {
      if (!loading) {
        refreshData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchStocktakes = async () => {
    try {
      setLoading(true);
      const stocktakeCollection = new Stocktakes();
      await stocktakeCollection.fetch();
      setStocktakes(stocktakeCollection.items);
    } catch (error) {
      console.error('Error fetching stocktakes:', error);
      toast.error('Lỗi tải danh sách kiểm kho');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setRefreshing(true);
      const stocktakeCollection = new Stocktakes();
      await stocktakeCollection.fetch();
      setStocktakes(stocktakeCollection.items);
    } catch (error) {
      console.error('Error refreshing stocktakes:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleAction = async (stocktakeId, action) => {
    console.log('Handling action:', action, 'for stocktake:', stocktakeId);
    
    try {
      setActionLoading(prev => ({ ...prev, [stocktakeId]: action }));
      
      let response;
      if (action === 'continue') {
        // Navigate to counting screen
        navigate(`/stocktakes/${stocktakeId}/count`);
        return;
      } else if (action === 'view' || action === 'approve' || action === 'reject') {
        // Navigate to detail page for viewing or approval actions
        navigate(`/stocktakes/${stocktakeId}`);
        return;
      } else if (action === 'edit') {
        // Navigate to edit page  
        navigate(`/stocktakes/${stocktakeId}/edit`);
        return;
      } else {
        // API actions (start, submit, approve, reject)
        console.log('Making API call for action:', action);
        response = await transitionStocktake(stocktakeId, action);
        console.log('API response:', response);
        
        if (response && response.status === 200) {
          toast.success(response.data?.message || `${action} thành công`);
          await refreshData(); // Refresh to get updated status
          
          // Auto-navigate to counting screen after starting
          if (action === 'start') {
            console.log('Navigating to counting screen');
            navigate(`/stocktakes/${stocktakeId}/count`);
            return;
          }
        } else {
          console.error('Unexpected response:', response);
          toast.error('Lỗi không mong đợi');
        }
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      
      // Check if it's the single active stocktake constraint error
      const errorMessage = error.response?.data?.error;
      if (errorMessage && errorMessage.includes('already an active stocktake')) {
        // Show detailed message for active stocktake constraint
        toast.error(errorMessage, {
          autoClose: 6000, // Show for 6 seconds since it's a longer message
          style: { whiteSpace: 'pre-wrap' }
        });
      } else {
        // Default error message
        toast.error(errorMessage || `Lỗi ${action}`);
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [stocktakeId]: false }));
    }
  };

  const handleCreateStocktake = async (data) => {
    try {
      const stocktakeCollection = new Stocktakes();
      const newStocktake = await stocktakeCollection.create(data);
      if (newStocktake) {
        toast.success('Tạo phiếu kiểm kho thành công');
        setCreateDialogOpen(false);
        await refreshData();
      }
    } catch (error) {
      console.error('Error creating stocktake:', error);
      
      // Check if it's the active stocktake constraint error
      const errorMessage = error.response?.data?.error;
      if (errorMessage && errorMessage.includes('already an active stocktake')) {
        // Don't close dialog, let it show the error
        // Re-throw to let dialog handle it
        throw error;
      } else {
        toast.error(errorMessage || 'Lỗi tạo phiếu kiểm kho');
      }
    }
  };

  // Filter stocktakes based on search and status
  const filteredStocktakes = stocktakes.filter(stocktake => {
    const matchesSearch = !searchTerm || 
      stocktake.stocktake_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      stocktake.created_by?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || stocktake.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getActionIcon = (action) => {
    switch (action) {
      case 'start': return <StartIcon />;
      case 'continue': return <ContinueIcon />;
      case 'view': return <ViewIcon />;
      case 'review': return <ApproveIcon />;
      default: return null;
    }
  };

  const renderStocktakeCard = (stocktake) => {
    const statusDisplay = stocktake.getStatusDisplay();
    const progress = stocktake.getProgressPercentage();
    const actions = stocktake.getAvailableActions(userRole);
    const varianceSummary = stocktake.getVarianceSummary();

    const isActive = stocktake.status === 'in_progress';
    
    return (
      <Grid item xs={12} sm={6} md={4} key={stocktake.id}>
        <Card sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          border: isActive ? '2px solid' : '1px solid',
          borderColor: isActive ? 'primary.main' : 'divider',
          boxShadow: isActive ? 3 : 1,
          position: 'relative'
        }}>
          {isActive && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              px: 1,
              py: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5
            }}>
              <Typography variant="caption" fontWeight="bold">
                ĐANG HOẠT ĐỘNG
              </Typography>
            </Box>
          )}
          <CardContent sx={{ flexGrow: 1, mt: isActive ? 3 : 0 }}>
            {/* Header with stocktake number and status */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Typography variant="h6" component="h2" noWrap>
                {stocktake.stocktake_number}
              </Typography>
              <Chip 
                label={statusDisplay.text} 
                color={statusDisplay.color} 
                size="small"
              />
            </Box>

            {/* Created by and date */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tạo bởi: {stocktake.created_by?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {stocktake.getFormattedDate()}
            </Typography>

            {/* Progress bar for in-progress stocktakes */}
            {stocktake.status === 'InProgress' && (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Tiến độ</Typography>
                  <Typography variant="body2">{progress}%</Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {stocktake.summary.counted_items}/{stocktake.summary.total_items} sản phẩm
                </Typography>
              </Box>
            )}

            {/* Summary info for completed stocktakes */}
            {(stocktake.status === 'Submitted' || stocktake.status === 'Approved') && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Tổng sản phẩm: {stocktake.summary.total_items}
                </Typography>
                {stocktake.summary.total_variance !== 0 && (
                  <Typography 
                    variant="body2" 
                    sx={{ color: varianceSummary.color }}
                  >
                    Chênh lệch: {varianceSummary.text}
                  </Typography>
                )}
              </Box>
            )}

            {/* Notes */}
            {stocktake.notes && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                "{stocktake.notes}"
              </Typography>
            )}
          </CardContent>

          {/* Action buttons */}
          <CardActions>
            {actions.map((action, index) => (
              <Button
                key={index}
                size="small"
                variant={action.variant}
                color={action.color}
                startIcon={getActionIcon(action.action)}
                onClick={() => handleAction(stocktake.id, action.action)}
                disabled={actionLoading[stocktake.id] === action.action}
                sx={{ mr: 1 }}
              >
                {actionLoading[stocktake.id] === action.action ? 'Đang xử lý...' : action.label}
              </Button>
            ))}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const renderLoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="rectangular" height={8} sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      {/* Header with search and filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Tìm theo số phiếu hoặc người tạo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Lọc theo trạng thái"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tất cả trạng thái</MenuItem>
              <MenuItem value="Draft">Nháp</MenuItem>
              <MenuItem value="InProgress">Đang kiểm</MenuItem>
              <MenuItem value="Submitted">Đã gửi</MenuItem>
              <MenuItem value="Approved">Đã duyệt</MenuItem>
              <MenuItem value="Rejected">Từ chối</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Tooltip title="Làm mới">
              <IconButton 
                onClick={refreshData} 
                disabled={refreshing}
                sx={{ mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              sx={{ mr: 2 }}
            >
              Bộ lọc nâng cao
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Tạo phiếu kiểm kho
            </Button>
          </Grid>
        </Grid>

        {/* Refresh indicator */}
        {refreshing && (
          <LinearProgress sx={{ mt: 1 }} />
        )}
      </Box>

      {/* Content */}
      {loading ? (
        renderLoadingSkeleton()
      ) : filteredStocktakes.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {searchTerm || statusFilter ? 
            'Không tìm thấy phiếu kiểm kho nào phù hợp với bộ lọc.' : 
            'Chưa có phiếu kiểm kho nào. Tạo phiếu đầu tiên ngay!'
          }
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredStocktakes.map(renderStocktakeCard)}
        </Grid>
      )}

      {/* Floating action button for creating new stocktake */}
      {/* Temporarily keeping FAB with debugging */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 9999,
        }}
      >
        <Fab
          color="primary"
          aria-label="Tạo phiếu kiểm kho"
          size="large"
          onClick={() => {
            console.log('FAB clicked - opening create dialog');
            setCreateDialogOpen(true);
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Create stocktake dialog */}
      <CreateStocktakeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateStocktake}
      />
    </Container>
  );
};

export default StocktakeList;